import React, { useEffect, useState } from 'react';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Button from '@cloudscape-design/components/button';
import Input from '@cloudscape-design/components/input';
import Table from '@cloudscape-design/components/table';
import FormField from '@cloudscape-design/components/form-field';
import Box from '@cloudscape-design/components/box';
import Alert from '@cloudscape-design/components/alert';
import TextFilter from '@cloudscape-design/components/text-filter';
import { api } from '../api/client';
import { useApi } from '../hooks/useApi';
import { ResponseViewer } from '../components/ResponseViewer';

interface EntityPageProps {
  app: string;
  title: string;
  endpoint: string;
  endpointPlural: string;
}

export const EntityPage: React.FC<EntityPageProps> = ({ app, title, endpoint, endpointPlural }) => {
  const [items, setItems] = useState<{ name: string; display_name?: string; description?: string }[]>([]);
  const [name, setName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [desc, setDesc] = useState('');
  const [filterText, setFilterText] = useState('');
  const { loading, result, execute } = useApi();

  const load = () => {
    if (!app) return;
    api('GET', `/${encodeURIComponent(app)}/${encodeURIComponent(endpointPlural)}`).then(d => { if (Array.isArray(d)) setItems(d); }).catch(() => {});
  };
  useEffect(load, [app]);

  if (!app) return <Alert type="info">Select an application from the top bar to manage {title.toLowerCase()}.</Alert>;

  const create = () => execute(async () => {
    const body: Record<string, string | null> = { name };
    if (displayName) body.display_name = displayName;
    if (desc) body.description = desc;
    const r = await api('POST', `/${encodeURIComponent(app)}/${encodeURIComponent(endpoint)}`, body);
    setName(''); setDisplayName(''); setDesc('');
    load();
    return r;
  });

  const remove = (n: string) => execute(async () => {
    const r = await api('DELETE', `/${encodeURIComponent(app)}/${encodeURIComponent(endpoint)}`, { name: n });
    load();
    return r;
  });

  const filtered = items.filter(item =>
    !filterText ||
    item.name.toLowerCase().includes(filterText.toLowerCase()) ||
    (item.display_name || '').toLowerCase().includes(filterText.toLowerCase()) ||
    (item.description || '').toLowerCase().includes(filterText.toLowerCase())
  );

  return (
    <SpaceBetween size="l">
      <Header variant="h1">{title}</Header>

      <Container header={<Header variant="h2">Create {title.slice(0, -1)}</Header>}>
        <SpaceBetween size="s">
          <SpaceBetween direction="horizontal" size="s">
            <FormField label="Name *">
              <Input value={name} onChange={({ detail }) => setName(detail.value)} placeholder="e.g. admin" />
            </FormField>
            <FormField label="Display Name">
              <Input value={displayName} onChange={({ detail }) => setDisplayName(detail.value)} placeholder="optional" />
            </FormField>
            <FormField label="Description">
              <Input value={desc} onChange={({ detail }) => setDesc(detail.value)} placeholder="optional" />
            </FormField>
          </SpaceBetween>
          <Button variant="primary" onClick={create} loading={loading} disabled={!name}>
            Create
          </Button>
        </SpaceBetween>
      </Container>

      <Table
        header={<Header counter={`(${filtered.length})`}>{title} in {app}</Header>}
        filter={
          <TextFilter
            filteringText={filterText}
            onChange={({ detail }) => setFilterText(detail.filteringText)}
            filteringPlaceholder={`Find ${endpointPlural}`}
          />
        }
        columnDefinitions={[
          { id: 'name', header: 'Name', cell: item => item.name, sortingField: 'name' },
          { id: 'display_name', header: 'Display Name', cell: item => item.display_name || '—' },
          { id: 'description', header: 'Description', cell: item => item.description || '—' },
          {
            id: 'actions',
            header: 'Actions',
            width: 120,
            cell: item => <Button variant="inline-link" onClick={() => remove(item.name)}>Delete</Button>,
          },
        ]}
        items={filtered}
        sortingDisabled={false}
        empty={<Box textAlign="center" padding="l">No {endpointPlural} found</Box>}
      />

      <ResponseViewer data={result?.data} error={result?.error} />
    </SpaceBetween>
  );
};
